// Hotkey registry — the single owner of keyboard accelerators, so the ⌘K palette,
// plugin command `hotkey`s, and future surface chords can't silently collide.
//
// Contract:
//  - register(chord, scope, handler) → unregister. FIRST registration wins: a
//    second claim on the same chord+scope is ignored and (in dev) warned. So an
//    outdated/duplicate binding degrades to "absent", never hijacks the first.
//  - `scope` is "global" (fires anywhere) or "surface" (surface-local). The two
//    scopes are independent namespaces, so a global ⌘K and a surface ⌘K never
//    collide — this is what lets the nav keep its own ⌘F.
//  - ONE window keydown listener, with input-focus suppression: no hotkey fires
//    while the user is typing in a field / code editor (shared isTypingTarget).
//
// Kept in a `.ts` (context + hook, Fast-Refresh clean); the provider lives here
// too as it holds no view.

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { createElement } from "react";
import { isTypingTarget } from "./hotkeyTarget.js";

export type HotkeyScope = "global" | "surface";

/** A parsed, order-independent chord — modifiers + the base key (lowercased).
 *  `mod` maps to ⌘ on mac, Ctrl elsewhere (resolved at match time). */
interface Chord {
  readonly mod: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly key: string;
}

/** Parse "mod+shift+k" → a Chord. Tokens are case-insensitive and order-free;
 *  the last non-modifier token is the base key. */
export function parseChord(chord: string): Chord {
  let mod = false;
  let shift = false;
  let alt = false;
  let key = "";
  for (const raw of chord.split("+")) {
    const t = raw.trim().toLowerCase();
    if (t === "mod" || t === "cmd" || t === "ctrl" || t === "meta" || t === "control") mod = true;
    else if (t === "shift") shift = true;
    else if (t === "alt" || t === "option" || t === "opt") alt = true;
    else if (t !== "") key = t;
  }
  return { mod, shift, alt, key };
}

/** A stable canonical string for a chord — the Map key. Order-independent. */
function chordKey(c: Chord): string {
  return `${c.mod ? "M" : ""}${c.shift ? "S" : ""}${c.alt ? "A" : ""}:${c.key}`;
}

/** Does a keyboard event match this chord? `mod` is ⌘ on mac, Ctrl elsewhere. */
function eventMatchesChord(e: KeyboardEvent, c: Chord): boolean {
  const modDown = e.metaKey || e.ctrlKey;
  if (c.mod !== modDown) return false;
  if (c.shift !== e.shiftKey) return false;
  if (c.alt !== e.altKey) return false;
  return e.key.toLowerCase() === c.key;
}

export interface HotkeyRegistry {
  /** Claim a chord for a scope. Returns an unregister fn. First-wins: a second
   *  claim on the same chord+scope is ignored (dev-warned) and its unregister is
   *  a no-op. */
  register(chord: string, scope: HotkeyScope, handler: (e: KeyboardEvent) => void): () => void;
}

interface Entry {
  readonly chord: Chord;
  readonly handler: (e: KeyboardEvent) => void;
}

/** Build a registry backed by a mutable map. Exposed for direct unit testing +
 *  used by the provider. `dispatch(e)` is what the window listener calls. */
export function createHotkeyRegistry(): HotkeyRegistry & { dispatch(e: KeyboardEvent): void } {
  // Keyed by `${scope}|${chordKey}` — scopes are independent namespaces.
  const entries = new Map<string, Entry>();

  const register: HotkeyRegistry["register"] = (chord, scope, handler) => {
    const parsed = parseChord(chord);
    const key = `${scope}|${chordKey(parsed)}`;
    if (entries.has(key)) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[hotkey] "${chord}" (${scope}) already claimed — ignoring the second binding.`);
      }
      return () => {};
    }
    entries.set(key, { chord: parsed, handler });
    return () => {
      // Only the owner clears it (identity guard against a late no-op unregister).
      if (entries.get(key)?.handler === handler) entries.delete(key);
    };
  };

  const dispatch = (e: KeyboardEvent) => {
    // Never hijack while typing in a field / code editor.
    if (isTypingTarget(e.target)) return;
    for (const entry of entries.values()) {
      if (eventMatchesChord(e, entry.chord)) {
        e.preventDefault();
        entry.handler(e);
        return;
      }
    }
  };

  return { register, dispatch };
}

const NOOP_REGISTRY: HotkeyRegistry = { register: () => () => {} };

const Ctx = createContext<HotkeyRegistry>(NOOP_REGISTRY);

/** The hotkey registry. Defaults to a NOOP registry, so a `useHotkey` outside a
 *  provider silently no-ops rather than throwing (viewer/SSR safe). */
export function useHotkeyRegistry(): HotkeyRegistry {
  return useContext(Ctx);
}

/** Mounts one window keydown listener bound to a fresh registry, and provides it.
 *  SSR/headless-guarded — the effect only runs in the browser. */
export function HotkeyRegistryProvider({ children }: { readonly children: ReactNode }): ReactNode {
  // Lazy-init so the registry (Map + closures) is built once, not per render.
  const ref = useRef<ReturnType<typeof createHotkeyRegistry> | null>(null);
  if (ref.current === null) ref.current = createHotkeyRegistry();
  const registry = ref.current;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => registry.dispatch(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [registry]);
  return createElement(Ctx.Provider, { value: registry }, children);
}

/** Register a chord for the lifetime of the calling component (while `enabled`).
 *  First-wins is enforced by the registry; a losing claim is a silent no-op. */
export function useHotkey(
  chord: string,
  scope: HotkeyScope,
  handler: (e: KeyboardEvent) => void,
  enabled = true,
): void {
  const registry = useHotkeyRegistry();
  // Keep the latest handler without re-registering (re-registration would lose
  // the first-wins claim to a re-mount race).
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    return registry.register(chord, scope, (e) => ref.current(e));
  }, [registry, chord, scope, enabled]);
}
